<template>
  <v-container fluid>
    <h2>
      {{ $tt('customProduct') }}
    </h2>
    <v-btn class="ma-3" small color="warning" @click="openPlantingEditor()">
      <v-icon>mdi-plus</v-icon>
      {{ $tt('customNewRegistration') }}
    </v-btn>

    <v-list three-line-line>
      <v-list-item v-for="p in plantings" :key="p.id">
        <v-list-item-avatar :color="p.colorHex"> </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>
            {{ p.getLongDisplayText() }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ p.duration }}
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            {{ $tt('customFieldCount', { count: p.fieldsCount }) }}
            /
            {{ p.fieldArea | aidAreaText }}
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-action>
          <div v-if="isMyPlanting(p)">
            <v-btn icon @click="openPlantingEditor(p)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon @click="openDeletePlantingDialog(p)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <!-- ------------ -->
    <!-- 作付編集ダイアログ -->
    <!-- ------------ -->
    <v-dialog v-model="showingPlantingEditor" max-width="80%" persistent>
      <v-card>
        <v-card-text class="pt-3">
          <!-- 作物/品種 -->
          <v-card class="mb-3" outlined>
            <v-card-title>
              <h6 class="subtitle-2">{{ $tt('customCrop') }}</h6>
            </v-card-title>
            <v-card-text class="py-0 my-0">
              <v-select
                :items="growCrops"
                item-text="localizedName"
                item-value="masterId"
                v-model="selectedMasterCropId"
                @change="onGrowCropSelectionChanged"
                :error="plantingEditorErrors.growCropSelect.error"
                :error-messages="plantingEditorErrors.growCropSelect.message"
              />
              <v-btn text class="mx-0 px-0" @click="toggleCropOptions()" small>
                {{ toggleCropOptionsButtonCaption }}
                <v-icon>mdi-chevron-double-right</v-icon>
              </v-btn>
              <v-card outlined v-if="showingCropOptions" class="mb-3">
                <v-card-text>
                  <v-text-field
                    :label="$tt('customPlantingCropDisplayName')"
                    v-model="plantingCropName"
                    :error="plantingEditorErrors.plantingCropNameInput.error"
                    :error-messages="plantingEditorErrors.plantingCropNameInput.message"
                  />
                  <v-text-field :label="$tt('customPlantingCropVarietyName')" v-model="plantingVarietyName" />
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
          <!-- 栽培期間 -->
          <v-row>
            <v-col>
              <v-card class="mb-3" outlined>
                <v-card-title>
                  <h6 class="subtitle-2">{{ $tt('customPlantingSeason') }}</h6>
                </v-card-title>
                <v-card-text class="py-0 my-0">
                  <v-row>
                    <v-col class="py-0">
                      <v-menu
                        ref="menuPlantingStartDate"
                        v-model="showingPlantingStartDateMenu"
                        :close-on-content-click="false"
                        :return-value.sync="plantingStartDate"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="plantingStartDate"
                            :label="$tt('customPlantingSeasonStartDate')"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error="plantingEditorErrors.plantingStartDateInput.error"
                            :error-messages="plantingEditorErrors.plantingStartDateInput.message"
                          />
                        </template>
                        <v-date-picker
                          v-model="plantingStartDate"
                          no-title
                          scrollable
                          :day-format="date => new Date(date).getDate()"
                          @input="$refs.menuPlantingStartDate.save(plantingStartDate)"
                        />
                      </v-menu>
                    </v-col>
                    <v-col class="py-0">
                      <v-menu
                        ref="menuPlantingEndDate"
                        v-model="showingPlantingEndDateMenu"
                        :close-on-content-click="false"
                        :return-value.sync="plantingEndDate"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="plantingEndDate"
                            :label="$tt('customPlantingSeasonEndDate')"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            clearable
                          />
                        </template>
                        <v-date-picker
                          v-model="plantingEndDate"
                          no-title
                          scrollable
                          :day-format="date => new Date(date).getDate()"
                          @input="$refs.menuPlantingEndDate.save(plantingEndDate)"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <!-- 色 -->
            <v-col sm="6">
              <v-card outlined>
                <v-card-title>
                  <h6 class="subtitle-2">{{ $tt('customColor') }}</h6>
                </v-card-title>
                <v-card-text>
                  <v-color-picker
                    v-model="plantingColorHex"
                    hide-canvas
                    hide-inputs
                    show-swatches
                    flat
                    width="100%"
                    swatches-max-height="150"
                  />
                </v-card-text>
              </v-card>
            </v-col>
            <!-- 圃場 -->
            <v-col sm="6">
              <v-card outlined>
                <my-field-select-component
                  :multiple="true"
                  v-model="selectedFieldIds"
                  :error="plantingEditorErrors.fieldSelect.error"
                  :error-message="plantingEditorErrors.fieldSelect.message"
                  field-value="id"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closePlantingEditor()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click.stop="createOrUpdatePlanting()">
            {{ plantingEditorSaveButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
     作付削除ダイアログ
     -->
    <v-dialog v-model="showingDeletePlantingDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ deletePlantingConfirmationMessage }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="showingDeletePlantingDialog = false">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="deletePlanting()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import MyFieldSelectComponent from '@/components/MyFieldSelectComponent'
import * as Constants from '@/lib/constants'
import i18n from '@/lib/i18n'
import UserInfo from '@/lib/user-info'

export default {
  name: 'PlantingListAsList',
  components: { MyFieldSelectComponent },
  mounted() {
    this.$store.dispatch('getAllFields')
    this.$store.dispatch('initPlantings')
  },
  data() {
    return {
      editingPlantingId: null,
      selectedMasterCropId: null,
      plantingStartDate: null,
      plantingEndDate: null,
      plantingCropName: null,
      plantingVarietyName: null,
      plantingColorHex: Constants.CROP_DEFAULT_COLOR,

      plantingEditorSaveButtonText: i18n.tt('customSaveAction'),
      showingPlantingStartDateMenu: false,
      showingPlantingEndDateMenu: false,
      showingPlantingEditor: false,
      showingCropOptions: false,

      plantingEditorErrors: {
        growCropSelect: { error: false, message: null },
        plantingCropNameInput: { error: false, message: null },
        plantingStartDateInput: { error: false, message: null },
        fieldSelect: { error: false, message: null },
      },

      toggleCropOptionsButtonCaption: i18n.tt('customPlantingAddCropOption'),

      plantingForDelete: null,
      showingDeletePlantingDialog: false,
      deletePlantingConfirmationMessage: null,

      selectedFieldIds: [],

      palette: Constants.CROP_COLORS,
    }
  },
  computed: {
    plantings() {
      return this.$store.getters.plantings
    },
    // FIXME GROW
    growCrops() {
      return this.$store.getters.growCrops
    },
    // fieldsWithNameAndMine() {
    //   const me = UserInfo.getUsername()
    //   return this.$store.getters.fields.filter(f => f.name).filter(f => f.username === me)
    // },
  },
  methods: {
    isMyPlanting(planting) {
      const me = UserInfo.getUsername()
      return planting.username === me
    },
    onGrowCropSelectionChanged() {
      // masterCropIdがその他の場合
      if (this.selectedMasterCropId === -1) {
        this.openCropOptions()
      } else {
        this.closeCropOptions()
      }
    },
    openCropOptions() {
      this.showingCropOptions = true
      this.toggleCropOptionsButtonCaption = i18n.tt('customPlantingRemoveCropOption')
    },

    closeCropOptions() {
      this.showingCropOptions = false
      this.toggleCropOptionsButtonCaption = i18n.tt('customPlantingAddCropOption')
    },

    toggleCropOptions() {
      if (this.showingCropOptions) {
        this.closeCropOptions()
      } else {
        this.openCropOptions()
      }
    },

    createOrUpdatePlanting() {
      // masterCropIdが未選択
      if (!this.selectedMasterCropId) {
        this.plantingEditorErrors.growCropSelect.error = true
        this.plantingEditorErrors.growCropSelect.message = i18n.tt('customPlantingCropSelectionRequiredError')
        return
      }

      // masterCropIdがその他の場合、作物名の入力は必須
      if (this.selectedMasterCropId === -1) {
        if (!this.plantingCropName) {
          this.openCropOptions()
          this.plantingEditorErrors.plantingCropNameInput.error = true
          this.plantingEditorErrors.plantingCropNameInput.message = i18n.tt(
            'customPlantingOtherCropSelectionRequiredError'
          )
          return
        }
      }

      // 開始日は必須
      if (!this.plantingStartDate) {
        this.plantingEditorErrors.plantingStartDateInput.error = true
        this.plantingEditorErrors.plantingStartDateInput.message = i18n.tt('customErrorStartDateRequired')
        return
      }

      // 圃場も必須
      if (this.selectedFieldIds.length === 0) {
        this.plantingEditorErrors.fieldSelect.error = true
        this.plantingEditorErrors.fieldSelect.message = i18n.tt('customErrorFieldRequired')
        return
      }

      const planting = {
        fieldIds: this.selectedFieldIds,
        masterCropId: this.selectedMasterCropId,
        cropName: this.plantingCropName,
        varietyName: this.plantingVarietyName,
        colorHex: this.plantingColorHex,
        startDate: this.plantingStartDate,
        endDate: this.plantingEndDate,
      }
      let params = {
        request: {
          planting: planting,
        },
        onSuccess: this.onPlantingSaveSuccess,
      }
      let actionName = 'createPlanting'
      if (this.editingPlantingId) {
        actionName = 'updatePlanting'
        params.request.id = this.editingPlantingId
      }

      this.$store.dispatch(actionName, params)
    },

    onPlantingSaveSuccess() {
      this.closePlantingEditor()
      this.$store.dispatch('initPlantings')
    },

    openPlantingEditor(planting) {
      if (planting) {
        this.selectedFieldIds = planting.fieldIds
        this.editingPlantingId = planting.id
        this.selectedMasterCropId = planting.masterCropId
        this.plantingCropName = planting.cropName
        this.plantingVarietyName = planting.varietyName
        this.plantingStartDate = planting.startDate
        this.plantingEndDate = planting.endDate
        this.plantingColorHex = planting.colorHex
      } else {
        this._initPlantingEditorValues()
      }
      this.showingPlantingEditor = true
      if (this.plantingCropName || this.plantingVarietyName) {
        this.openCropOptions()
      } else {
        this.closeCropOptions()
      }
      this.clearPlantingEditorErrors()
    },

    clearPlantingEditorErrors() {
      for (const objectKey in this.plantingEditorErrors) {
        this.plantingEditorErrors[objectKey] = { error: false, message: null }
      }
    },

    closePlantingEditor() {
      this._initPlantingEditorValues()
      this.showingPlantingEditor = false
    },

    _initPlantingEditorValues() {
      this.selectedFieldIds = []
      this.editingPlantingId = null
      this.selectedMasterCropId = null
      this.plantingCropName = null
      this.plantingVarietyName = null
      this.plantingStartDate = null
      this.plantingEndDate = null
      this.plantingColorHex = Constants.CROP_DEFAULT_COLOR
    },

    deletePlanting() {
      const params = {
        request: {
          id: this.plantingForDelete.id,
        },
        onSuccess: this.onPlantingDeleteSuccess,
      }
      this.$store.dispatch('deletePlanting', params)
    },

    openDeletePlantingDialog(planting) {
      this.plantingForDelete = planting
      const params = {
        cropYear: planting.startDateYear,
        cropName: planting.cropName,
        cropVariety: planting.varietyName,
      }
      const plantingName = planting.getLongDisplayText({ displayYear: false })
      this.deletePlantingConfirmationMessage = i18n.tt('customPlantingDeleteConfirmationMessage', { plantingName })
      this.showingDeletePlantingDialog = true
    },

    closeDeletePlantingDialog() {
      this.plantingForDelete = null
      this.showingDeletePlantingDialog = false
    },

    onPlantingDeleteSuccess() {
      this.closeDeletePlantingDialog()
      this.$store.dispatch('initPlantings')
    },
  },
}
</script>
